import React, { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Maximize2, BedDouble, MapPin, X, ArrowRight, Phone, TrendingUp } from "lucide-react";
import { realisations, companyInfo } from "../data/mockData";

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const RealisationsPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAfter, setShowAfter] = useState(false);

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FAF9F6] relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#C4956A]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
              {"Nos réalisations"}
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A1A2E] mt-4">
              Des projets{" "}
              <span className="text-[#C4956A]">sur-mesure</span>
            </h1>
            <p className="text-lg text-[#1A1A2E]/60 mt-6 leading-relaxed">
              {"Chaque investissement est unique. Découvrez nos projets réalisés à Marseille, pensés pour valoriser votre bien et répondre à vos objectifs."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(realisations) ? realisations.map((project, i) => (
              <AnimatedSection key={project.id}>
                <div
                  className="group cursor-pointer"
                  onClick={() => {
                    setSelectedProject(project);
                    setShowAfter(false);
                  }}
                >
                  <div className="relative h-72 overflow-hidden rounded-2xl">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-[#1A1A2E]">
                        {project.type}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white font-semibold">{project.name}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-[#1A1A2E]">
                      {project.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#1A1A2E]/50">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E8DDD3]/50">
                      {project.rentabilite && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-[#C4956A]">
                          <TrendingUp className="w-3.5 h-3.5" />
                          {project.rentabilite}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                        <Maximize2 className="w-3.5 h-3.5" />
                        {project.surface}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                        <BedDouble className="w-3.5 h-3.5" />
                        {project.type}
                      </span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            )) : null}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.name}
                  className="w-full h-80 object-cover rounded-t-2xl"
                />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                >
                  <X className="w-5 h-5 text-[#1A1A2E]" />
                </button>
                {/* Avant/Après toggle — masqué temporairement, sera réactivé plus tard
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <button
                    onClick={() => setShowAfter(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !showAfter
                        ? "bg-[#C4956A] text-white"
                        : "bg-white/80 text-[#1A1A2E]"
                    }`}
                  >
                    Avant
                  </button>
                  <button
                    onClick={() => setShowAfter(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      showAfter
                        ? "bg-[#C4956A] text-white"
                        : "bg-white/80 text-[#1A1A2E]"
                    }`}
                  >
                    {"Après"}
                  </button>
                </div>
                */}
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold text-[#1A1A2E]">
                  {selectedProject.name}
                </h2>
                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  {selectedProject.rentabilite && (
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#C4956A]">
                      <TrendingUp className="w-4 h-4" />
                      {selectedProject.rentabilite}
                    </span>
                  )}
                  <span className="flex items-center gap-2 text-sm text-[#1A1A2E]/60">
                    <MapPin className="w-4 h-4" />
                    {selectedProject.location}
                  </span>
                  <span className="flex items-center gap-2 text-sm text-[#1A1A2E]/60">
                    <Maximize2 className="w-4 h-4" />
                    {selectedProject.surface}
                  </span>
                  <span className="flex items-center gap-2 text-sm text-[#1A1A2E]/60">
                    <BedDouble className="w-4 h-4" />
                    {selectedProject.type}
                  </span>
                </div>
                <p className="text-[#1A1A2E]/60 mt-4 text-sm leading-relaxed">
                  {"Projet réalisé par Key Vest Massilia dans le quartier "}{selectedProject.location}{" à Marseille. Rénovation complète, ameublement et mise en location clé en main."}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-[#1A1A2E] rounded-3xl p-12 lg:p-16 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                {"Votre projet pourrait être le"}{" "}
                <span className="text-[#C4956A]">prochain</span>
              </h2>
              <p className="text-white/60 max-w-md mx-auto mt-4">
                {"Contactez-nous pour discuter de votre investissement et découvrir comment nous pouvons vous accompagner."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all"
                >
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default RealisationsPage;
